import React, { useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';

import Dialog from '../../shared/PFDialog/PFDialogContainer';
import useToken from '../../../hooks/useToken';
import { prepareDashboardFilters } from '../../../utils/Helpers';
import { useAlerts } from '../../shared/Alerts/alertsService';
import {
  setPageHeaderComponent,
  setTabs,
  setClientId,
} from '../../../redux/slices/page-header.slice';
import { checkDashboardGlobalFilterApplied } from '../../../utils/global-filter.helper';
import DashboardList from '../../Analytics/Dashboard/DashboardList';
import PageHeaderBreadcrumb from '../../shared/PageHeader/PageHeaderBreadcrumb';

import {
  getMyDashboardDetails,
  deleteUnlinkDashboard,
  linkUserDashboard,
  updateTabOrder,
  saveHomeDashboardApi,
  updateDashboardLastVisitInfo,
} from './services/HomeDashboard.service';
import DraggableTabs from './components/DraggableTabs';
import AddViewEditDashboard from './AddViewEditDashboard';

const HomeDashboardContainer = () => {
  const breadcrumbs = [
    {
      url: '/dashboard',
      label: 'Home & Help',
      icon: 'pi pi-home',
    },
    {
      url: '/dashboard',
      label: 'Dashboard',
    },
    {
      url: '/dashboard',
      label: 'Overview',
    },
  ];

  const pathname = window.location.pathname;
  const { accessToken } = useToken();
  const [loading, setLoading] = React.useState();
  const [dashboardData, setDashboardData] = React.useState([]);
  const [value, setValue] = React.useState('newTab');
  const childRef = useRef();

  const [addViewEditDashboardKey, setAddViewEditDashboardKey] =
    React.useState(true);
  const [saveDisabled, setSaveDisabled] = useState(true);

  const [applyFilters, setApplyFilters] = React.useState(false);

  const [widgetFilterQuery, setWidgetFilterQuery] = React.useState([]);

  const [attachRemoveDashboardDialogOpen, setAttachRemoveDashboardDialogOpen] =
    React.useState(false);
  const [clearFilters, setClearFilters] = React.useState(false);
  const [homeFilters, setHomeFilters] = React.useState([]);
  const [homeFilterDateDimension, setHomeFilterDateDimension] = React.useState(
    []
  );

  const dispatch = useDispatch();
  const { activeDashboard, tabs, clientId } = useSelector(
    state => state.pageHeader
  );

  const [selectedItems, setSelectedItems] = useState([]);
  const [defaultDashboardId, setDefaultDashboardId] = useState(0);
  const { setAlert } = useAlerts();

  const toast = useRef(null);
  const [allTabs, setAllTabs] = useState([]);
  const timeoutRef = useRef(null);
  //const sidebarLayout = JSON.parse(localStorage.getItem('sidebar-layout'));
  const globalFilters = useSelector(state => state.globalFilters);
  const pageHeader = useSelector(state => state.pageHeader);

  const memoFilterState = useMemo(
    () => ({
      defaultFilter: globalFilters?.defaultFilter?.length,
      stores: globalFilters?.stores?.length,
      projectTypes: globalFilters?.projectTypes?.length,
      projectCategories: globalFilters?.projectCategories?.length,
      projectWorkrooms: globalFilters?.projectWorkrooms?.length,
      projectStatus: globalFilters?.projectStatus?.length,
      districtId: globalFilters?.districtId?.length,
      techniciansVal: globalFilters?.techniciansVal?.length,
      company: globalFilters?.company?.length,
      dateScheduledStart: globalFilters?.dateScheduledFilterStart,
      dateScheduledEnd: globalFilters?.dateScheduledFilterEnd,
      dateCompletionStart: globalFilters?.dateCompletionFilterStart,
      dateCompletionEnd: globalFilters?.dateCompletionFilterEnd,
      checkDateFrom: globalFilters?.checkDateFromFilter,
      checkDateTo: globalFilters?.checkDateToFilter,
      dateSoldFrom: globalFilters?.dateSoldFromFilter,
      dateSoldTo: globalFilters?.dateSoldToFilter,
      isDashboardApplicable: globalFilters?.isDashboardApplicable,
    }),
    [
      globalFilters?.defaultFilter,
      globalFilters?.stores,
      globalFilters?.projectTypes,
      globalFilters?.projectCategories,
      globalFilters?.projectWorkrooms,
      globalFilters?.projectStatus,
      globalFilters?.districtId,
      globalFilters?.techniciansVal,
      globalFilters?.company,
      globalFilters?.dateScheduledFilterStart,
      globalFilters?.dateScheduledFilterEnd,
      globalFilters?.dateCompletionFilterStart,
      globalFilters?.dateCompletionFilterEnd,
      globalFilters?.checkDateFromFilter,
      globalFilters?.checkDateToFilter,
      globalFilters?.dateSoldFromFilter,
      globalFilters?.dateSoldToFilter,
      globalFilters?.isDashboardApplicable,
    ]
  );
  const updateDefaultDashboardId = id => {
    setDefaultDashboardId(id);
    setSaveDisabled(false);
  };

  React.useEffect(() => {
    if (
      axios.defaults.headers.common['client-id'] &&
      globalFilters?.isDashboardApplicable
    ) {
      const appliedFilters = prepareDashboardFilters(globalFilters);
      setHomeFilters(appliedFilters?.[0]);
      setHomeFilterDateDimension(appliedFilters?.[1]);
      setApplyFilters(true);
      refreshDashboardList();
    }
  }, [
    memoFilterState,
    axios.defaults.headers.common['client-id'],
    pageHeader?.activeDashboard?.id,
  ]);

  React.useEffect(() => {
    if (axios.defaults.headers.common['client-id']) {
      getMyDashboardDetails(
        setLoading,
        setDashboardData,
        handleTabChange,
        pathname
      );
    }
  }, [axios.defaults.headers.common['client-id']]);

  React.useEffect(() => {
    if (clientId === '')
      dispatch(setClientId(String(localStorage.getItem('client_id'))));
  }, []);

  React.useEffect(() => {
    if (axios.defaults.headers.common['client-id'] && !activeDashboard?.name) {
      const currentDashboard = dashboardData?.find(
        dashboard => dashboard?.dashboard_id?.toString() === value
      );
      dispatch(
        setPageHeaderComponent({
          activeDashboard: {
            id: currentDashboard?.dashboard_id,
            name: currentDashboard?.name,
          },
        })
      );
    }
  }, [value]);
  const hasFilteredRef = React.useRef(false);

  React.useEffect(() => {
    if (
      pathname === '/dashboard-widget' &&
      dashboardData?.length > 0 &&
      !hasFilteredRef.current
    ) {
      const filteredData = dashboardData.filter(
        item => item.dashboard_id === activeDashboard.id
      );
      if (filteredData?.length === 0) {
        window.location.href = '/access-denied';
        return;
      }
      setDashboardData(filteredData);
      setAllTabs([
        {
          id: 0,
          name: filteredData[0]?.name,
          value: filteredData[0]?.dashboard_id,
        },
      ]);
      setValue(filteredData[0]?.dashboard_id);
      hasFilteredRef.current = true;
    }
  }, [pathname, dashboardData, activeDashboard]);

  const handleTabChange = newValue => {
    const currentDashboard = dashboardData?.find(
      dashboard => dashboard?.dashboard_id?.toString() == newValue
    );
    dispatch(
      setPageHeaderComponent({
        activeDashboard: {
          id: currentDashboard?.dashboard_id || activeDashboard?.id,
          name: currentDashboard?.name || activeDashboard?.name,
        },
      })
    );
    if (currentDashboard?.dashboard_id || activeDashboard?.id) {
      handleUpdateDashboardLastVisitInfo(
        currentDashboard?.dashboard_id || activeDashboard?.id
      );
    }
    setValue(newValue);
    childRef?.current?.handleClearAll();
  };

  const detachDashboard = dashboardId => {
    deleteUnlinkDashboard(
      dashboardId,
      setAttachRemoveDashboardDialogOpen,
      setLoading,
      setDashboardData,
      setValue,
      setAlert
    );
  };

  const handleAddIcon = () => {
    setAttachRemoveDashboardDialogOpen(true);
  };

  const sortTabs = tabData => {
    const tempTabData = [...tabData];
    tempTabData.sort((a, b) => {
      if (a.is_default_dashboard !== b.is_default_dashboard) {
        return b.is_default_dashboard - a.is_default_dashboard;
      }
      return a.sort_order - b.sort_order;
    });
    return tempTabData;
  };

  const getDefaultSelectedItems = () => {
    const tempSelecteditem = dashboardData.map(el => {
      return {
        id: el.dashboard_id,
        name: el.name,
        description: el.description,
        created_at: el.created_at,
        sort_order: el.DashboardMappings[0].sort_order,
        is_default_dashboard: el.DashboardMappings[0].is_default_dashboard,
      };
    });
    const sortedSelectedItem = sortTabs(tempSelecteditem);
    setSelectedItems(sortedSelectedItem);
    if (tabs === null || tabs?.[String(clientId)]?.length === 0) {
      const tabVal = sortedSelectedItem.map((el, idx) => {
        return { name: el.name, value: el.id, id: idx };
      });
      dispatch(setTabs({ [String(clientId)]: tabVal }));
    }
  };

  React.useEffect(() => {
    if (tabs && Object.keys(tabs).includes(clientId) == false)
      dispatch(setTabs({ ...tabs, [String(clientId)]: [] }));
  }, [clientId]);

  React.useEffect(() => {
    if (dashboardData?.length > 0) {
      getDefaultSelectedItems();
    } else if (selectedItems?.length > 0) {
      setSelectedItems([]);
      dispatch(setTabs({ ...tabs, [String(clientId)]: [] }));
    }
  }, [dashboardData]);

  React.useEffect(() => {
    if (tabs && pathname !== '/dashboard-widget') {
      setAllTabs(tabs?.[String(clientId)]);
    }
  }, [tabs]);

  React.useEffect(() => {
    const selectedDashboard = new Set(selectedItems.map(el => el.id));
    const existingDashboard = new Set(
      (dashboardData ?? []).map(el => el.dashboard_id)
    );
    const toRemove = new Set(
      [...existingDashboard].filter(x => !selectedDashboard.has(x))
    );
    const toLink = new Set(
      [...selectedDashboard].filter(x => !existingDashboard.has(x))
    );

    if (toRemove.size > 0 || toLink.size > 0) {
      setSaveDisabled(false);
    } else {
      setSaveDisabled(true);
    }
  }, [selectedItems, dashboardData]);

  const handleApiCall = () => {
    const sortedSelectedItem = sortTabs(selectedItems);
    // Make an API call with the selectedItems
    if (dashboardData?.length > 0) {
      const selectedDashboard = new Set(sortedSelectedItem.map(el => el.id));
      if (!selectedDashboard?.size) {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Select atleast one Dashboard',
          life: 3000,
        });
      } else {
        saveHomeDashboard({
          dashboardIds: [...selectedDashboard]?.toString(),
          defaultDashboardId: defaultDashboardId,
        });
        const tabVal = sortedSelectedItem.map((el, idx) => {
          return { name: el.name, value: el.id, id: idx };
        });
        dispatch(setTabs({ ...tabs, [String(clientId)]: tabVal }));
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Updated dashboard list',
          life: 3000,
        });
      }
    } else {
      sortedSelectedItem.forEach(element => {
        linkDashboard(element.id);
      });
      const tabVal = sortedSelectedItem.map((el, idx) => {
        return { name: el.name, value: el.id, id: idx };
      });
      dispatch(setTabs({ ...tabs, [String(clientId)]: tabVal }));
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Updated dashboard list',
        life: 3000,
      });
    }
  };

  const handleTabState = val => {
    dispatch(setTabs({ ...tabs, [String(clientId)]: val }));

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const updateTabData = val.map((el, idx) => {
        return { dashboard_id: parseInt(el.value), sort_order: idx };
      });
      updateTabOrder(setLoading, updateTabData, setAlert);
    }, 1000);
    return () => clearTimeout(timeoutRef.current);
  };

  const linkDashboard = dashboardId => {
    setLoading(true);
    const allIds =
      allTabs?.length === 0
        ? selectedItems.map(val => val.id)
        : allTabs.map(val => val.value);
    const sortOrder = allIds.indexOf(dashboardId);
    linkUserDashboard(
      dashboardId,
      setAttachRemoveDashboardDialogOpen,
      setLoading,
      setDashboardData,
      setValue,
      setAlert,
      sortOrder
    );
  };

  const saveHomeDashboard = async dashboardObj => {
    setLoading(true);
    const response = await saveHomeDashboardApi(dashboardObj);
    if (response) {
      getMyDashboardDetails(setLoading, setDashboardData, setValue);
      setAttachRemoveDashboardDialogOpen(false);
      setAlert('success', 'Dashboard saved successfully');
    }
  };

  const handleUpdateDashboardLastVisitInfo = async dashboardId => {
    await updateDashboardLastVisitInfo(dashboardId);
  };

  const handleCloseDialogue = () => {
    setAttachRemoveDashboardDialogOpen(false);
    getDefaultSelectedItems();
  };

  const refreshDashboardList = () => {
    setAddViewEditDashboardKey(!addViewEditDashboardKey);
  };

  // const setView = () => {
  //   setDashboardView('old');
  // };
  const buttonArray = [
    // {
    //   label: 'Classic View',
    //   icon: 'pi pi-table',
    //   outlined: false,
    //   size: 'small',
    //   action: setView,
    //   visible: true,
    // },
    {
      label: 'Refresh',
      icon: 'pi pi-refresh',
      outlined: false,
      size: 'small',
      action: refreshDashboardList,
      visible: true,
    },
  ];
  const content = (
    <div className="flex align-items-center">
      <i className="pi pi-send" />
      <div className="m-2">
        Your data is being shown with applied global filters.
      </div>
    </div>
  );
  const authVerification = () => {
    document.location = `${document.location.origin}/verify-pin`;
    return null;
  };

  return !accessToken ? (
    authVerification()
  ) : (
    <>
      <PageHeaderBreadcrumb
        headerValue="Dashboard Overview"
        breadcrumbs={breadcrumbs}
        buttonArray={buttonArray}
        identifier="dashboard"
      />
      <div className="grid col-12 mx-0 my-0">
        {checkDashboardGlobalFilterApplied(globalFilters)?.isFilterApplied && (
          <Message
            content={content}
            className="p-2"
            style={{
              border: 'solid #3b82f6',
              borderWidth: '0 0 0 6px',
              color: '#3b82f6',
            }}
          />
        )}
        <div className="grid col-12 surface-50 pb-6 mx-0 my-0 p-0">
          {dashboardData?.length
            ? dashboardData.map((tab, index) => {
                const activeTabPanel =
                  activeDashboard?.id &&
                  (pathname === '/dashboard-widget' ||
                    pathname === '/dashboard')
                    ? activeDashboard?.id.toString()
                    : value;
                if (tab?.DashboardMappings?.[0]?.is_display)
                  return (
                    <>
                      {activeTabPanel.toString() ==
                        tab.dashboard_id.toString() && (
                        <AddViewEditDashboard
                          id={index}
                          key={addViewEditDashboardKey}
                          type="home"
                          dashboardId={tab.dashboard_id}
                          setHomeDashboardLoading={setLoading}
                          homeDashboardLoading={loading}
                          setWidgetFilterQuery={setWidgetFilterQuery}
                          applyFilters={applyFilters}
                          widgetFilterQuery={widgetFilterQuery}
                          setApplyFilters={setApplyFilters}
                          clearFilters={clearFilters}
                          setClearFilters={setClearFilters}
                          homeFilters={homeFilters}
                          setHomeFilters={setHomeFilters}
                          homeFilterDateDimension={homeFilterDateDimension}
                          setHomeFilterDateDimension={
                            setHomeFilterDateDimension
                          }
                          refreshDashboardList={refreshDashboardList}
                        />
                      )}
                    </>
                  );
              })
            : null}
        </div>

        {dashboardData?.length ? (
          <footer className="dashboard-tabs-footer pf-tab-view fixed bottom-0">
            {value ? (
              <DraggableTabs
                tabsValue={
                  activeDashboard?.id &&
                  (pathname === '/dashboard-widget' ||
                    pathname === '/dashboard')
                    ? activeDashboard?.id.toString()
                    : value
                }
                tabsOption={{
                  variant: 'scrollable',
                  scrollButtons: 'auto',
                  ariaLabel: 'scrollable auto tabs',
                }}
                allTabs={allTabs}
                onTabClick={handleTabChange}
                valueProperty="dashboard_id"
                nameProperty="name"
                handleAddIcon={handleAddIcon}
                showAdd={true}
                handleTabState={handleTabState}
                setLoading={setLoading}
                loading={loading}
                setWidgetFilterQuery={setWidgetFilterQuery}
                applyFilters={applyFilters}
                widgetFilterQuery={widgetFilterQuery}
                setApplyFilters={setApplyFilters}
                clearFilters={clearFilters}
                setClearFilters={setClearFilters}
                homeFilters={homeFilters}
                setHomeFilters={setHomeFilters}
                homeFilterDateDimension={homeFilterDateDimension}
                setHomeFilterDateDimension={setHomeFilterDateDimension}
                refreshDashboardList={refreshDashboardList}
              />
            ) : null}
          </footer>
        ) : null}

        <Toast ref={toast} />

        <Dialog
          header="Dashboard List"
          visible={attachRemoveDashboardDialogOpen}
          style={{ minHeight: '80vh', maxWidth: '80vw' }}
          onHide={() => handleCloseDialogue()}
          draggable={false}
          footer={
            <div className="p-2">
              <Button
                label="Save"
                onClick={handleApiCall}
                className="p-button-primary"
                disabled={saveDisabled}
              />
            </div>
          }
          className="w-full"
        >
          <DashboardList
            source="home"
            dashboardData={dashboardData}
            setHomeLoading={setLoading}
            linkDashboard={linkDashboard}
            detachDashboard={detachDashboard}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            updateDefaultDashboardId={updateDefaultDashboardId}
            defaultDashboardId={defaultDashboardId}
          />
        </Dialog>
      </div>
    </>
  );
};

export default HomeDashboardContainer;
